import React, { useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import {
    LinkIcon,
    ListBulletIcon,
    MagnifyingGlassIcon,
    PaperClipIcon,
    PhotoIcon
} from '@heroicons/react/24/outline';
import { ALLOWED_IMAGE_MIME } from '../../rich-editor/parts/editorHelpers';
import { isGifConfigured } from '../services/gifProvider';
import { sendKeyToggleLabel } from '../utils/sendOnEnter';
import { SendKeyIcon } from './SendKeyIcon';

interface ToolbarButtonProps {
    active: boolean;
    onClick: () => void;
    disabled?: boolean;
    title: string;
    children: React.ReactNode;
    ariaLabel?: string;
    ariaPressed?: boolean;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
    active,
    onClick,
    disabled,
    title,
    children,
    ariaLabel,
    ariaPressed
}) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={title}
        aria-label={ariaLabel}
        aria-pressed={ariaPressed}
        className={`w-7 h-7 flex items-center justify-center rounded text-xs font-semibold transition-colors ${
            active
                ? 'bg-gray-800 text-white'
                : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700'
        } disabled:opacity-30 disabled:cursor-not-allowed`}
    >
        {children}
    </button>
);

interface LinkPopoverProps {
    initialUrl: string;
    initialLabel: string;
    onSubmit: (url: string, label: string) => void;
    onRemove: () => void;
    onClose: () => void;
}

const LinkPopover: React.FC<LinkPopoverProps> = ({
    initialUrl,
    initialLabel,
    onSubmit,
    onRemove,
    onClose
}) => {
    const [label, setLabel] = useState(initialLabel);
    const [url, setUrl] = useState(initialUrl);
    const urlRef = useRef<HTMLInputElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        urlRef.current?.focus();
        if (initialUrl) urlRef.current?.select();
    }, []);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const trimmedUrl = url.trim();
        if (!trimmedUrl) {
            onRemove();
        } else {
            onSubmit(
                trimmedUrl.startsWith('http') ? trimmedUrl : `https://${trimmedUrl}`,
                label.trim()
            );
        }
    };

    return (
        <div
            ref={popoverRef}
            className="bg-white border border-gray-200 rounded-lg shadow-sm p-2.5"
        >
            <form onSubmit={handleSubmit} className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                    <label className="text-[11px] font-medium text-gray-500 w-10 shrink-0">
                        Label
                    </label>
                    <input
                        type="text"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="Link text"
                        className="flex-1 text-sm border border-gray-300 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                                e.stopPropagation();
                                onClose();
                            }
                        }}
                    />
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-[11px] font-medium text-gray-500 w-10 shrink-0">
                        URL
                    </label>
                    <input
                        ref={urlRef}
                        type="text"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://example.com"
                        className="flex-1 text-sm border border-gray-300 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                                e.stopPropagation();
                                onClose();
                            }
                        }}
                    />
                </div>
                <div className="flex items-center justify-end gap-1.5">
                    {initialUrl && (
                        <button
                            type="button"
                            onClick={onRemove}
                            className="text-xs text-red-500 hover:text-red-600 font-medium px-2 py-1 rounded transition-colors mr-auto"
                        >
                            Remove
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-xs text-gray-500 hover:text-gray-700 font-medium px-2 py-1 rounded transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={!url.trim()}
                        className="text-xs font-semibold text-white bg-gray-800 hover:bg-gray-700 rounded-md px-3 py-1.5 transition-colors disabled:opacity-50"
                    >
                        Apply
                    </button>
                </div>
            </form>
        </div>
    );
};

interface InsertImagesButtonProps {
    onPick: (files: File[]) => void;
    disabled?: boolean;
}

const InsertImagesButton: React.FC<InsertImagesButtonProps> = ({ onPick, disabled }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    return (
        <>
            <ToolbarButton
                active={false}
                onClick={() => inputRef.current?.click()}
                disabled={disabled}
                title="Insert image"
                ariaLabel="Insert image"
            >
                <PhotoIcon className="w-3.5 h-3.5" />
            </ToolbarButton>
            <input
                ref={inputRef}
                type="file"
                className="hidden"
                accept={ALLOWED_IMAGE_MIME.join(',')}
                multiple
                onChange={(e) => {
                    onPick(Array.from(e.target.files ?? []));
                    e.target.value = '';
                }}
            />
        </>
    );
};

interface EditorToolbarProps {
    editor: Editor | null;
    linkPopoverOpen?: boolean;
    onToggleLinkPopover?: () => void;
    onInsertImages?: (files: File[]) => void;
    emojiPickerOpen?: boolean;
    onToggleEmojiPicker?: () => void;
    gifPickerOpen?: boolean;
    onToggleGifPicker?: () => void;
    isSearchOpen?: boolean;
    onToggleSearch?: () => void;
    onFileChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    sending?: boolean;
    canSend?: boolean;
    onSend?: () => void;
    sendOnEnter?: boolean;
    onToggleSendOnEnter?: () => void;
    trailingActions?: React.ReactNode;
    tools?: React.ReactNode;
}

const EditorToolbar: React.FC<EditorToolbarProps> = ({
    editor,
    linkPopoverOpen,
    onToggleLinkPopover,
    onInsertImages,
    emojiPickerOpen,
    onToggleEmojiPicker,
    gifPickerOpen,
    onToggleGifPicker,
    isSearchOpen,
    onToggleSearch,
    onFileChange,
    sending,
    canSend,
    onSend,
    sendOnEnter,
    onToggleSendOnEnter,
    trailingActions,
    tools
}) => {
    if (!editor) return null;

    return (
        <div className="flex items-center gap-0.5 px-0 py-1">
            <ToolbarButton
                active={editor.isActive('bold')}
                onClick={() => editor.chain().focus().toggleBold().run()}
                title="Bold (Ctrl+B)"
            >
                B
            </ToolbarButton>
            <ToolbarButton
                active={editor.isActive('italic')}
                onClick={() => editor.chain().focus().toggleItalic().run()}
                title="Italic (Ctrl+I)"
            >
                <span className="italic">I</span>
            </ToolbarButton>
            {onToggleLinkPopover && (
                <ToolbarButton
                    active={editor.isActive('link') || !!linkPopoverOpen}
                    onClick={onToggleLinkPopover}
                    title="Link"
                >
                    <LinkIcon className="w-3.5 h-3.5" />
                </ToolbarButton>
            )}

            <div className="w-px h-4 bg-gray-200 mx-0.5" />

            <ToolbarButton
                active={editor.isActive('bulletList')}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                title="Bullet list"
            >
                <ListBulletIcon className="w-3.5 h-3.5" />
            </ToolbarButton>
            <ToolbarButton
                active={editor.isActive('orderedList')}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                title="Numbered list"
            >
                <svg
                    className="w-3.5 h-3.5"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                >
                    <text
                        x="1"
                        y="5"
                        fontSize="5"
                        fill="currentColor"
                        stroke="none"
                        fontFamily="sans-serif"
                    >
                        1.
                    </text>
                    <line x1="7" y1="3.5" x2="15" y2="3.5" />
                    <text
                        x="1"
                        y="10"
                        fontSize="5"
                        fill="currentColor"
                        stroke="none"
                        fontFamily="sans-serif"
                    >
                        2.
                    </text>
                    <line x1="7" y1="8.5" x2="15" y2="8.5" />
                    <text
                        x="1"
                        y="15"
                        fontSize="5"
                        fill="currentColor"
                        stroke="none"
                        fontFamily="sans-serif"
                    >
                        3.
                    </text>
                    <line x1="7" y1="13.5" x2="15" y2="13.5" />
                </svg>
            </ToolbarButton>
            <ToolbarButton
                active={editor.isActive('code')}
                onClick={() => editor.chain().focus().toggleCode().run()}
                title="Inline code"
            >
                <svg
                    className="w-3.5 h-3.5"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                >
                    <polyline points="5,3 1,8 5,13" />
                    <polyline points="11,3 15,8 11,13" />
                </svg>
            </ToolbarButton>

            {(onInsertImages || onToggleEmojiPicker) && (
                <div className="w-px h-4 bg-gray-200 mx-0.5" />
            )}
            {onInsertImages && <InsertImagesButton onPick={onInsertImages} disabled={sending} />}
            {onToggleEmojiPicker && (
                <ToolbarButton
                    active={!!emojiPickerOpen}
                    onClick={onToggleEmojiPicker}
                    title="Emoji"
                >
                    <svg
                        className="w-3.5 h-3.5"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                    >
                        <circle cx="8" cy="8" r="6.5" />
                        <circle cx="6" cy="6.5" r="0.75" fill="currentColor" stroke="none" />
                        <circle cx="10" cy="6.5" r="0.75" fill="currentColor" stroke="none" />
                        <path
                            d="M5.5 9.5C6 10.5 7 11 8 11C9 11 10 10.5 10.5 9.5"
                            strokeLinecap="round"
                        />
                    </svg>
                </ToolbarButton>
            )}
            {onToggleGifPicker && (
                <ToolbarButton
                    active={!!gifPickerOpen}
                    onClick={onToggleGifPicker}
                    disabled={!isGifConfigured}
                    title={isGifConfigured ? 'GIF' : 'GIF (not configured)'}
                >
                    <span className="text-[10px] font-bold leading-none">GIF</span>
                </ToolbarButton>
            )}
            {tools}

            {/* Right side: app trailing actions, send-mode toggle, search, attach, send */}
            <div className="ml-auto flex items-center gap-0.5">
                {trailingActions}
                {onToggleSendOnEnter && (
                    <ToolbarButton
                        active={!!sendOnEnter}
                        onClick={onToggleSendOnEnter}
                        title={sendKeyToggleLabel(!!sendOnEnter)}
                        ariaLabel={sendKeyToggleLabel(!!sendOnEnter)}
                        ariaPressed={!!sendOnEnter}
                    >
                        <SendKeyIcon className="w-3.5 h-3.5" />
                    </ToolbarButton>
                )}
                {onToggleSearch && (
                    <ToolbarButton
                        active={!!isSearchOpen}
                        onClick={onToggleSearch}
                        title="Search messages"
                    >
                        <MagnifyingGlassIcon className="w-3.5 h-3.5" />
                    </ToolbarButton>
                )}
                {onFileChange && (
                    <label
                        className={`w-7 h-7 flex items-center justify-center rounded text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors ${
                            sending ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'
                        }`}
                        title="Attach file"
                    >
                        <input
                            type="file"
                            className="hidden"
                            onChange={onFileChange}
                            multiple
                            disabled={sending}
                        />
                        <PaperClipIcon className="w-3.5 h-3.5" />
                    </label>
                )}
                {onSend && (
                    <button
                        type="button"
                        onClick={onSend}
                        disabled={!canSend}
                        className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ml-1 ${
                            canSend
                                ? 'bg-yellow-400 hover:bg-yellow-300 text-gray-800'
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                        title="Send"
                    >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M1.5 1.5L14.5 8L1.5 14.5V9.5L10 8L1.5 6.5V1.5Z" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
};

export { EditorToolbar, LinkPopover };
